/**
 * Download Proxy Utility
 *
 * Downloads external files via a server-side proxy to bypass CORS restrictions.
 * Works with GCS, S3, CloudFront, fal.ai, RunPod CDN URLs.
 *
 * The proxy endpoint (GET /api/v1/download/proxy) validates the URL domain
 * against an allowlist and returns the file with Content-Disposition: attachment.
 */

import { apiClient } from '@/services/apiClient';
import { getApiBaseUrl } from '@/services/urlResolver';
import { isDesktopApp } from '@/utils/apiMode';
import { saveMediaFile } from '@/utils/electronBridge';

/**
 * Check if a URL is external (cross-origin) and needs proxy download.
 */
function isExternalUrl(url: string): boolean {
  if (!url) return false;
  if (url.startsWith('data:') || url.startsWith('blob:')) return false;
  if (url.startsWith('/')) return false;

  try {
    const parsed = new URL(url);
    // URLs pointing at our own API backend are directly fetchable: Electron
    // (file:// origin) bypasses CORS for the local backend, and the web dev
    // backend sets CORS for the dev origin. Only TRULY foreign origins
    // (GCS / S3 / CDN) need the server-side proxy.
    try {
      if (parsed.origin === new URL(getApiBaseUrl()).origin) return false;
    } catch {
      // getApiBaseUrl misconfigured — fall through to the same-origin check.
    }
    return parsed.origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Resolve a URL to a Blob, transparently routing external (cross-origin)
 * URLs through the server-side proxy to bypass CORS, and fetching
 * same-origin / data / blob URLs directly.
 *
 * Shared by `downloadFile` (web anchor-click download) and the Electron
 * desktop download path (`useResultDownload`), which both need the exact
 * same byte-resolution logic before diverging on how the bytes are saved.
 *
 * @param url - The file URL to resolve
 * @throws Error if the fetch fails
 */
export async function resolveUrlToBlob(url: string): Promise<Blob> {
  if (isExternalUrl(url)) {
    // Use server-side proxy to bypass CORS
    const response = await apiClient.get('/api/v1/download/proxy', {
      params: { url },
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data;
  }

  // Same-origin or data/blob URL: fetch directly
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Download failed: ${response.status}`);
  return response.blob();
}

/**
 * Convert a Blob to a `data:` URL string via FileReader.
 *
 * @param blob - The blob to convert
 * @throws Error if the FileReader fails to read the blob
 */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Download a file via server-side proxy and trigger browser download.
 *
 * For external URLs (GCS, S3, CDN), uses the backend proxy to bypass CORS.
 * For same-origin or data/blob URLs, downloads directly.
 *
 * @param url - The file URL to download
 * @param filename - Filename for the downloaded file
 * @throws Error if download fails
 */
export async function downloadFile(url: string, filename: string): Promise<void> {
  const blob = await resolveUrlToBlob(url);
  const blobUrl = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
}

/**
 * Save a file whose content was generated in the browser (a transcript, an
 * export, a serialized project) rather than fetched from the backend.
 *
 * On the desktop build this goes through the native save dialog instead of
 * anchor-clicking a blob. Anchor-clicking hands the file to Chromium's own
 * download manager, whose "Save As" window can open BEHIND the app — the user
 * sees nothing happen and clicks again.
 *
 * @param content  - The file's text content.
 * @param filename - Suggested filename, including extension.
 * @param mimeType - MIME type of the content.
 * @returns true if the file was written, false if the user cancelled.
 * @throws Error if saving failed for any other reason — callers must not
 *         report success unconditionally (all three Speech-to-Text buttons
 *         used to show "Downloaded!" even when nothing was written).
 */
export async function saveGeneratedTextFile(
  content: string,
  filename: string,
  mimeType: string = 'text/plain'
): Promise<boolean> {
  const blob = new Blob([content], { type: mimeType });

  if (isDesktopApp()) {
    const dataUrl = await blobToDataUrl(blob);
    const result = await saveMediaFile(dataUrl, filename);
    if (result.canceled) return false;
    if (!result.success) {
      throw new Error(result.error ?? 'Failed to save file');
    }
    return true;
  }

  const blobUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return true;
  } finally {
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
  }
}
